import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, UtensilsCrossed, Car, ShoppingBag, Home, Stethoscope, Heart, Check, Plus, Loader2, CalendarDays } from 'lucide-react';
import AppLogo from '../components/AppLogo';

const CATEGORIES = [
  { id: 'food', label: 'Food', icon: UtensilsCrossed, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', placeholder: 'e.g., Dinner Tuesday, groceries from Trader Joe\'s...' },
  { id: 'transportation', label: 'Transportation', icon: Car, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', placeholder: 'e.g., Ride to oncology appointment Thursday 2pm...' },
  { id: 'errands', label: 'Errands', icon: ShoppingBag, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', placeholder: 'e.g., Pick up prescription from CVS, return library books...' },
  { id: 'home_support', label: 'Home Support', icon: Home, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', placeholder: 'e.g., Laundry, yard work, help with dishes...' },
  { id: 'accompaniment', label: 'Accompaniment to Appointments', icon: Stethoscope, color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300', placeholder: 'e.g., Come with me to Dr. Kim on Monday, sit with me during infusion...' },
  { id: 'cheerleading', label: 'Cheerleading / Commiseration', icon: Heart, color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', placeholder: 'e.g., Call me Wednesday evening, send me a funny meme, just check in...\n\nRemember: no fixers! Just support.' },
];

const PlanWeekPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [circles, setCircles] = useState([]);
  const [tasks, setTasks] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCircles = async () => {
      try {
        const res = await axios.get(`${API}/circles`);
        setCircles(res.data);
      } catch (e) { console.warn('Circles not loaded yet'); }
      finally { setLoading(false); }
    };
    fetchCircles();
  }, []);

  const handleAddTask = async (categoryId) => {
    const text = tasks[categoryId]?.trim();
    if (!text) { toast.error('Type what you need first'); return; }
    if (!circles[0]) { toast.error('Create a circle first to add tasks'); return; }

    setSubmitting(prev => ({ ...prev, [categoryId]: true }));
    try {
      const category = CATEGORIES.find(c => c.id === categoryId);
      await axios.post(`${API}/tasks`, {
        title: text,
        description: `Category: ${category.label}`,
        circle_id: circles[0].id,
        priority: 'medium',
      });
      setSubmitted(prev => ({ ...prev, [categoryId]: [...(prev[categoryId] || []), text] }));
      setTasks(prev => ({ ...prev, [categoryId]: '' }));
      toast.success('Posted to your squad!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add task');
    } finally {
      setSubmitting(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const totalPosted = Object.values(submitted).flat().length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="plan-week-page">
      <header className="surface-header sticky top-0 z-40 border-b border-cyan-100">
        <div className="max-w-2xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Plan Your Week</h1>
            <p className="text-xs text-muted-foreground">Think through what you need — your squad is ready</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        <div className="text-center space-y-2 pb-2">
          <CalendarDays className="w-10 h-10 text-cyan-500 mx-auto" />
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
            What do you need help with this week? Go through each category and post requests for your care squad.
          </p>
        </div>

        {CATEGORIES.map((cat) => (
          <div key={cat.id} className="surface-card rounded-xl overflow-hidden" data-testid={`category-${cat.id}`}>
            <div className="px-5 py-3 border-b border-border bg-muted/20 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cat.color}`}>
                <cat.icon className="w-4.5 h-4.5" />
              </div>
              <h3 className="text-sm font-semibold text-foreground">{cat.label}</h3>
            </div>
            <div className="p-4 space-y-3">
              {submitted[cat.id]?.map((text, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2">
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{text}</span>
                </div>
              ))}
              <Textarea
                placeholder={cat.placeholder}
                value={tasks[cat.id] || ''}
                onChange={(e) => setTasks(prev => ({ ...prev, [cat.id]: e.target.value }))}
                className="resize-none rounded-xl text-sm min-h-[60px]"
                rows={2}
                data-testid={`input-${cat.id}`}
              />
              <Button
                size="sm"
                className="btn-primary w-full"
                onClick={() => handleAddTask(cat.id)}
                disabled={submitting[cat.id] || !tasks[cat.id]?.trim()}
                data-testid={`submit-${cat.id}`}
              >
                {submitting[cat.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                Post to Squad
              </Button>
            </div>
          </div>
        ))}

        {totalPosted > 0 && (
          <div className="text-center pt-4 space-y-3">
            <p className="text-sm font-medium text-foreground">
              You posted {totalPosted} request{totalPosted !== 1 ? 's' : ''} this week!
            </p>
            <Button className="btn-primary" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanWeekPage;
